import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import {
  DwStatsData,
  statsInterval,
} from "api/interfaces/widgetInterface.interface";
import { useMemo, useState } from "react";
import { useAppSelector } from "redux/hooks";
import { useQuery } from "react-query";
import {
  CategoryDef,
  LegendType,
} from "api/interfaces/dashboardInterface.interface";
import {
  calcStoragePercent,
  convertBytesWithUnit,
  formatBytesAndUnits,
  formatNumber,
} from "utils/functions";
import moment from "moment";
import { C } from "utils/StatsUtil";
import { useStatsWidget } from "pages/dashboard/stats/graph/GridChartItem";
import { convertDateToFormat } from "utils/timeUtil";

interface Props {
  queryFn: (args: {
    accountId: string;
    interval: statsInterval;
    includeSuspended: boolean;
  }) => Promise<any>;
  queryName: string;
  items: CategoryDef[];
  unit?: "percent" | "bytes" | "number";
}

export default function useStatsData({
  queryFn,
  queryName,
  items,
  unit = "number",
}: Props) {
  const [interval, setInterval] = useState<statsInterval>("DAILY");
  const { includeSuspended } = useStatsWidget();
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const statsData = useQuery(
    [queryName, selectedAccount.accountId, interval, includeSuspended],
    () =>
      queryFn({
        accountId: selectedAccount.accountId,
        interval: interval,
        includeSuspended: includeSuspended ?? false,
      }),
    {
      retry: 0,
      refetchOnWindowFocus: false,
    }
  );
  // @todo API 추후 변경 필요
  const data = useMemo(() => {
    const result: DwStatsData[] = Array.isArray(statsData.data?.result)
      ? statsData.data?.result
      : statsData.data?.result?.stats ?? [];
    if (result.length === 0) {
      return [];
    }
    let lists = result
      .sort((a, b) => {
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      })
      .map((item) => {
        if (queryName === "cloudStorage") {
          const newItem = {
            ...item,
            unusedStorage:
              Number(item.totalStorage ?? 0) - Number(item.usedStorage ?? 0),
          };
          return newItem;
        }
        return item;
      });
    if (interval === "DAILY") {
      // 동일 date 문자열이 API에서 중복될 수 있음 → 하루 한 행만 유지(마지막 값 우선)
      const byDate = new Map<string, DwStatsData>();
      for (const row of lists) {
        byDate.set(row.date, row);
      }
      lists = [...byDate.values()].sort((a, b) => {
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      });
      const nextDayIso = new Date(lists.at(-1)?.date ?? "");
      const nextDayFormatted = convertDateToFormat(moment(nextDayIso, "America/Los_Angeles")
        .add(1, "day"), selectedAccount.dateFormat);
      const todayFormatted = convertDateToFormat(moment()
        .tz("America/Los_Angeles"), selectedAccount.dateFormat);

      if (
        nextDayFormatted &&
        lists.findIndex((item) => item.date === nextDayFormatted) === -1 &&
        !moment(todayFormatted).isBefore(moment(nextDayFormatted))
      ) {
        const lastRow = lists.at(-1) ?? ({} as DwStatsData);
        let todayData: DwStatsData = {
          date: nextDayFormatted,
        };
        items.forEach((item) => {
          todayData = {
            ...todayData,
            [item.key]:
              statsData.data?.result?.[item.key] ??
              (lastRow as any)?.[item.key] ??
              0,
          };
        });
        lists.push(todayData);
      }
      if (lists.length > 14) {
        return lists.slice(lists.length - 14, lists.length);
      }
    }
    return lists;
  }, [
    statsData.data?.result,
    interval,
    queryName,
    selectedAccount.dateFormat,
    items,
  ]);
  const legend: LegendType[] = useMemo(() => {
    const total = items.reduce(
      (acc, curr) => acc + Number(statsData.data?.result[curr.key] as number),
      0
    );
    let l: LegendType[] = (items || []).map((item) => {
      const rawValue = (statsData.data?.result[item.key] as number) || 0;
      let convertedValue: string =
        unit === "bytes"
          ? (() => {
              const { value, unit: byteUnit } = formatBytesAndUnits(rawValue);
              return `${value} ${byteUnit}`;
            })()
          : unit === "percent"
          ? `${calcStoragePercent(rawValue, total)}%`
          : String(formatNumber(rawValue));
      if (queryName === "cloudStorage" && item.key === "unusedStorage") {
        convertedValue = convertBytesWithUnit(
          Number(statsData.data?.result?.totalStorage ?? 0) -
            Number(statsData.data?.result?.usedStorage ?? 0)
        );
      }
      return {
        label: item.label,
        value: convertedValue,
        color: item.color ?? "",
        key: item.key,
      };
    });
    if (queryName === "cloudStorage") {
      l.push({
        label: "Total",
        value: convertBytesWithUnit(
          Number(statsData.data?.result?.totalStorage ?? 0)
        ),
        color: C.blue,
      });
    }
    return l;
  }, [items, queryName, statsData.data?.result, unit]);
  return {
    statsData,
    data,
    legend,
    interval,
    setInterval,
  };
}
